'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  useForm,
  type FieldErrors,
  type UseFormHandleSubmit,
  type UseFormRegister,
} from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { LinkButton } from '@/components/ui/Button/LinkButton';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { RECAPTCHA_SITE_KEY } from '@/lib/const';
import { createForgotPasswordSchema, type ForgotPasswordFormData } from '@/lib/forms/schemas/auth';
import { getRecaptchaLanguageFromLocale } from '@/lib/i18n/config';
import styles from '../authForm.module.scss';
import drawerStyles from './ForgotPasswordForm.drawer.module.scss';
import pageStyles from './ForgotPasswordForm.module.scss';

// Hoisted rich-text chunk renderers — defined at module scope so they aren't
// recreated on every render (and to avoid defining components inside a component).
const renderBreak = () => <br />;
const renderEmailHighlight = (chunks: ReactNode) => (
  <span className={styles.emailHighlight}>{chunks}</span>
);
const drawerRenderEmailHighlight = (chunks: ReactNode) => (
  <span className={drawerStyles.emailHighlight}>{chunks}</span>
);

export interface ForgotPasswordFormProps {
  /** `page` — standalone `/my-account/forgot-password`; `drawer` — in-drawer layout */
  readonly variant?: 'page' | 'drawer';
  /** Drawer only: returns to the login sub-view */
  readonly onBack?: () => void;
  /** Drawer only: parent can hide duplicate headings when email is sent */
  readonly onEmailSentChange?: (emailSent: boolean) => void;
}

interface ForgotPasswordConfirmationProps {
  readonly email: string;
  readonly variant: 'page' | 'drawer';
  readonly onBack?: () => void;
}

function ForgotPasswordConfirmation({ email, variant, onBack }: ForgotPasswordConfirmationProps) {
  const t = useTranslations('myAccount.forgotPassword');
  const tCommon = useTranslations('common');
  const isDrawer = variant === 'drawer';

  return (
    <output className={clsx(styles.confirmationMessage, isDrawer && drawerStyles.confirmation)}>
      <h2
        className={clsx(styles.confirmationHeading, isDrawer && drawerStyles.confirmationHeading)}
      >
        {t('confirmationTitle')}
      </h2>
      <p className={clsx(styles.confirmationText, isDrawer && drawerStyles.confirmationText)}>
        {t.rich('confirmationText', {
          email,
          strong: isDrawer ? drawerRenderEmailHighlight : renderEmailHighlight,
          br: renderBreak,
        })}
      </p>
      <div className={clsx(isDrawer && drawerStyles.confirmationActions)}>
        <LinkButton
          href="/my-account/login"
          variant="secondary"
          size={isDrawer ? 'md' : 'lg'}
          fullWidth={isDrawer}
          className={isDrawer ? drawerStyles.confirmationButton : undefined}
          iconAfter={<Icon name="chevron-right" size={20} aria-hidden="true" />}
        >
          {t('backToSignIn')}
        </LinkButton>
        {isDrawer && onBack ? (
          <button type="button" className={drawerStyles.backButton} onClick={onBack}>
            <Icon name="chevron-left" size={20} aria-hidden="true" />
            <span>{tCommon('goBack')}</span>
          </button>
        ) : null}
      </div>
    </output>
  );
}

interface ForgotPasswordFieldsProps {
  readonly variant: 'page' | 'drawer';
  readonly onBack?: () => void;
  readonly register: UseFormRegister<ForgotPasswordFormData>;
  readonly errors: FieldErrors<ForgotPasswordFormData>;
  readonly isValid: boolean;
  readonly isSubmitting: boolean;
  readonly isCaptchaRequired: boolean;
  readonly captchaToken: string | null;
  readonly recaptchaRef: RefObject<ReCAPTCHA>;
  readonly recaptchaLang: string;
  readonly onCaptchaChange: (token: string | null) => void;
  readonly handleSubmit: UseFormHandleSubmit<ForgotPasswordFormData>;
  readonly onSubmit: (data: ForgotPasswordFormData) => Promise<void>;
}

function ForgotPasswordFields({
  variant,
  onBack,
  register,
  errors,
  isValid,
  isSubmitting,
  isCaptchaRequired,
  captchaToken,
  recaptchaRef,
  recaptchaLang,
  onCaptchaChange,
  handleSubmit,
  onSubmit,
}: ForgotPasswordFieldsProps) {
  const t = useTranslations('myAccount.forgotPassword');
  const tLogin = useTranslations('myAccount.login');
  const tCommon = useTranslations('common');
  const isDrawer = variant === 'drawer';
  const canSubmit = isValid && (!isCaptchaRequired || Boolean(captchaToken));

  return (
    <>
      {variant === 'page' ? (
        <p className={styles.description}>{t.rich('description', { br: renderBreak })}</p>
      ) : null}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={clsx(styles.form, isDrawer && drawerStyles.form)}
        noValidate
      >
        <div className={clsx(styles.fieldsWrapper, isDrawer && drawerStyles.fieldsWrapper)}>
          <Input
            {...register('email')}
            type="email"
            label={variant === 'page' ? t('email') : undefined}
            placeholder={isDrawer ? tLogin('email') : undefined}
            autoComplete="email"
            required
            error={errors.email?.message}
            hasError={!!errors.email}
            fullWidth
            disabled={isSubmitting}
          />

          {isCaptchaRequired ? (
            <div
              className={clsx(pageStyles.captchaWrapper, isDrawer && drawerStyles.captchaWrapper)}
            >
              <ReCAPTCHA
                key={recaptchaLang}
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                hl={recaptchaLang}
                onChange={onCaptchaChange}
                onExpired={() => onCaptchaChange(null)}
              />
            </div>
          ) : null}
        </div>

        <div className={clsx(styles.actions, isDrawer && drawerStyles.actions)}>
          {isDrawer && onBack ? (
            <button type="button" className={drawerStyles.backButton} onClick={onBack}>
              <Icon name="chevron-left" size={20} aria-hidden="true" />
              <span>{tCommon('goBack')}</span>
            </button>
          ) : null}
          <Button
            type="submit"
            variant="secondary"
            size={isDrawer ? 'md' : 'lg'}
            fullWidth={variant === 'page'}
            className={clsx(styles.resetButton, isDrawer && drawerStyles.submitButton)}
            loading={isSubmitting}
            disabled={isSubmitting || !canSubmit}
            iconAfter={
              isSubmitting ? undefined : <Icon name="chevron-right" size={16} aria-hidden="true" />
            }
          >
            {t('submit')}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * Forgot-password form shared by the account page and {@link MyAccountDrawer}.
 *
 * Submits to `/api/auth/forgot-password`. Always shows the confirmation state
 * afterwards — including on network failure, so the response never reveals
 * whether the address is registered — with a CTA to the login page.
 */
export function ForgotPasswordForm({
  variant = 'page',
  onBack,
  onEmailSentChange,
}: ForgotPasswordFormProps) {
  const locale = useLocale();
  const tForms = useTranslations('forms');
  const recaptchaLang = getRecaptchaLanguageFromLocale(locale);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const isCaptchaRequired = Boolean(RECAPTCHA_SITE_KEY);

  const schema = useMemo(
    () =>
      createForgotPasswordSchema({
        requiredField: tForms('requiredField'),
        invalidEmailAddress: tForms('invalidEmailAddress'),
      }),
    [tForms]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    if (isCaptchaRequired && !captchaToken) {
      return;
    }

    setIsSubmitting(true);

    try {
      await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          locale,
          ...(captchaToken ? { recaptchaToken: captchaToken } : {}),
        }),
      });

      setSubmittedEmail(data.email);
      setEmailSent(true);
      onEmailSentChange?.(true);
    } catch {
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
      setSubmittedEmail(data.email);
      setEmailSent(true);
      onEmailSentChange?.(true);
    }

    setIsSubmitting(false);
  };

  if (emailSent) {
    return <ForgotPasswordConfirmation email={submittedEmail} variant={variant} onBack={onBack} />;
  }

  return (
    <ForgotPasswordFields
      variant={variant}
      onBack={onBack}
      register={register}
      errors={errors}
      isValid={isValid}
      isSubmitting={isSubmitting}
      isCaptchaRequired={isCaptchaRequired}
      captchaToken={captchaToken}
      recaptchaRef={recaptchaRef}
      recaptchaLang={recaptchaLang}
      onCaptchaChange={setCaptchaToken}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
    />
  );
}

ForgotPasswordForm.displayName = 'ForgotPasswordForm';
